import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';
import type { SchemaField } from '../../ProcessDefinition/types';
import type { 
  SchemaState, 
  SchemaOperations, 
  AdvancedEditorState 
} from '../types';
import { parseBackendSchema, fieldsToInputSchema, fieldsToOutputSchema } from '../../ProcessDefinition/types';
import { OPMApi } from '../../../../../api/opmApi';

interface UseSchemaEditorProps {
  nodeId: string;
  ontologyId: number;
  definition?: any;
  type: 'input' | 'output';
  onSave?: (data: SchemaField[]) => void;
}

export function useSchemaEditor({
  nodeId,
  ontologyId,
  definition,
  type,
  onSave
}: UseSchemaEditorProps) {
  // 使用ref避免闭包陷阱
  const currentEditFieldRef = useRef<SchemaField | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 状态管理
  const [schemaState, setSchemaState] = useState<SchemaState>({
    dataSource: [],
    loading: false,
    hasChanges: false,
    error: null
  });

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [advancedState, setAdvancedState] = useState<AdvancedEditorState>({
    visible: false,
    currentField: null,
    tempData: null
  });

  // 清理函数
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // 深拷贝工具函数 - 使用更安全的方法
  const deepCloneSchemaFields = useCallback((fields: SchemaField[]): SchemaField[] => {
    return JSON.parse(JSON.stringify(fields));
  }, []);

  // 初始化数据
  useEffect(() => {
    if (!definition) return;

    // 取消之前的请求
    cleanup();
    
    // 创建新的abort controller
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const schemaKey = type === 'input' ? 'inputSchema' : 'outputSchema';
      const schemaData = definition[schemaKey];
      
      console.log(`[useSchemaEditor] ${type} schema 原始数据:`, schemaData);
      
      if (schemaData) {
        const fields = parseBackendSchema(schemaData);
        console.log(`[useSchemaEditor] ${type} schema 解析后字段:`, fields);
        const clonedFields = deepCloneSchemaFields(fields);
        
        // 检查组件是否仍然挂载
        if (!abortController.signal.aborted) {
          setSchemaState(prev => ({
            ...prev,
            dataSource: clonedFields,
            hasChanges: false,
            error: null
          }));
        }
      } else {
        if (!abortController.signal.aborted) {
          setSchemaState(prev => ({
            ...prev,
            dataSource: [],
            hasChanges: false,
            error: null
          }));
        }
      }
    } catch (error) {
      if (!abortController.signal.aborted) {
        console.error(`Failed to parse ${type} schema:`, error);
        setSchemaState(prev => ({
          ...prev,
          error: `解析${type === 'input' ? '输入' : '输出'}schema失败`,
          dataSource: []
        }));
      }
    }

    return cleanup;
  }, [definition, type, deepCloneSchemaFields, cleanup]);

  // 统一的字段更新函数 - 解决race condition
  const updateField = useCallback((key: string, updates: Partial<SchemaField>) => {
    console.log('updateField called:', { key, updates });
    setSchemaState(prev => {
      const updateItem = (items: SchemaField[]): SchemaField[] => {
        return items.map(item => {
          if (item.key === key) {
            return {
              ...item,
              ...updates,
              constraints: { 
                ...item.constraints, 
                ...(updates.constraints || {}) 
              }
            };
          }
          if (item.children) {
            return { 
              ...item, 
              children: updateItem(item.children) 
            };
          }
          return item;
        });
      };

      const updatedDataSource = updateItem(prev.dataSource);
      
      // 同时更新advancedState中的currentField，确保UI同步
      setAdvancedState(advancedPrev => {
        if (!advancedPrev.currentField) return advancedPrev;
        const currentOriginalKey = (advancedPrev.currentField as any).originalKey || advancedPrev.currentField.key;
        if (currentOriginalKey === key) {
          // 递归查找匹配originalKey的字段
          const findFieldByOriginalKey = (items: SchemaField[], targetKey: string): SchemaField | undefined => {
            for (const item of items) {
              const originalKey = (item as any).originalKey || item.key;
              if (originalKey === targetKey) return item;
              if (item.children) {
                const found = findFieldByOriginalKey(item.children, targetKey);
                if (found) return found;
              }
            }
            return undefined;
          };
          const updatedField = findFieldByOriginalKey(updatedDataSource, key);
          if (updatedField) {
            return {
              ...advancedPrev,
              currentField: {
                ...advancedPrev.currentField,
                ...updatedField,
                constraints: { ...advancedPrev.currentField.constraints, ...updatedField.constraints }
              },
              tempData: { ...updatedField }
            };
          }
        }
        return advancedPrev;
      });

      return {
        ...prev,
        dataSource: updatedDataSource,
        hasChanges: true,
        error: null
      };
    });
  }, []);

  // 切换必填状态
  const toggleRequired = useCallback((key: string) => {
    setSchemaState(prev => {
      const updateItem = (items: SchemaField[]): SchemaField[] => {
        return items.map(item => {
          if (item.key === key) {
            return {
              ...item,
              required: !item.required
            };
          }
          if (item.children) {
            return { 
              ...item, 
              children: updateItem(item.children) 
            };
          }
          return item;
        });
      };

      return {
        ...prev,
        dataSource: updateItem(prev.dataSource),
        hasChanges: true
      };
    });
  }, []);

  // 切换数组模式
  const toggleArray = useCallback((key: string) => {
    setSchemaState(prev => {
      const updateItem = (items: SchemaField[]): SchemaField[] => {
        return items.map(item => {
          if (item.key === key) {
            return {
              ...item,
              isArray: !item.isArray
            };
          }
          if (item.children) {
            return { 
              ...item, 
              children: updateItem(item.children) 
            };
          }
          return item;
        });
      };

      return {
        ...prev,
        dataSource: updateItem(prev.dataSource),
        hasChanges: true
      };
    });
  }, []);

  // 更新描述
  const updateDescription = useCallback((key: string, description: string) => {
    updateField(key, { description });
  }, [updateField]);

  // 展开/收起
  const toggleExpand = useCallback((key: string) => {
    setExpandedKeys(prev => {
      const newKeys = new Set(prev);
      if (newKeys.has(key)) {
        newKeys.delete(key);
      } else {
        newKeys.add(key);
      }
      return newKeys;
    });
  }, []);

  // 打开高级编辑器
  const openAdvancedEditor = useCallback((field: SchemaField) => {
    currentEditFieldRef.current = field;
    setAdvancedState({
      visible: true,
      currentField: field,
      tempData: { ...field }
    });
  }, []);

  // 关闭高级编辑器
  const closeAdvancedEditor = useCallback(() => {
    currentEditFieldRef.current = null;
    setAdvancedState({
      visible: false,
      currentField: null,
      tempData: null
    });
  }, []);

  // 保存数据
  const save = useCallback(async () => {
    if (!onSave) {
      message.warning('保存函数未提供');
      return;
    }

    // 取消之前的操作
    cleanup();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setSchemaState(prev => ({ ...prev, loading: true, error: null }));

      // 检查是否被取消
      if (abortController.signal.aborted) return;

      // 调用父组件的保存函数
      await onSave?.(schemaState.dataSource);
      
      setSchemaState(prev => ({
        ...prev,
        hasChanges: false,
        loading: false
      }));

    } catch (error: any) {
      if (abortController.signal.aborted) return;
      
      setSchemaState(prev => ({
        ...prev,
        loading: false,
        error: error.message || '保存失败'
      }));
      message.error(error.message || '保存失败');
    }
  }, [onSave, schemaState.dataSource, cleanup]);

  // 组件卸载时清理
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // 导出操作接口
  const operations: SchemaOperations = {
    updateField,
    toggleRequired,
    toggleArray,
    updateDescription
  };

  return {
    // 状态
    schemaState,
    expandedKeys,
    advancedState,
    
    // 操作
    operations,
    toggleExpand,
    openAdvancedEditor,
    closeAdvancedEditor,
    save,
    
    // 工具函数
    deepCloneSchemaFields
  };
}
